// src/Pages/Admin/Components/Card-admin/FichaCliente.jsx
// Ficha completa de un cliente: qué servicio eligió, sus reservas, sus sesiones,
// sus inspiraciones y todo lo que falta para poder generar el contrato.
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Camera,
  Calendar,
  Check,
  Clock,
  CreditCard,
  FileText,
  ImageOff,
  Images,
  Inbox,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Plus,
  Save,
  Sparkles,
  User,
  Video,
  Wallet,
  X,
} from 'lucide-react';
import { supabase } from '../../../../Supabase/SupabaseClient';
import {
  CAMPOS_CLIENTE,
  ESTADO_LABELS,
  ESTADOS,
  ETIQUETAS_CLIENTE,
  HUECO,
  datosFaltantes,
  generarContratoPDF,
  obtenerMonto,
  obtenerServicio,
  contactarClientePorDatos,
  abrirContacto,
} from '../Contratos/contratoPdf';
import styles from './FichaCliente.module.css';

// No embebemos `usuarios` en `ordenes` ni `reservas`: tienen varias FK a la vez
// y PostgREST se confunde. Los datos del cliente llegan por props y los servicios
// se resuelven contra el catálogo, igual que hace la pestaña Contratos.
const SELECT_ORDEN =
  'id, usuario_id, servicio_id, paquete_id, adicional_id, cantidad, precio_unitario, montoTotal, metodoPago, estado, fecha_creacion';

const SELECT_SESION =
  'id, nombre, fecha, descripcion, estado, usuario_id, subido_google_drive';

// OJO: no seleccionar `path` de forma explícita, la columna puede no existir y
// PostgREST hace fallar la consulta entera. Ver `utils/fotosService.js`.
const SELECT_FOTOS = 'id, sesion_id, url_foto, orden, destacada';

const CAMPOS_ORDEN_EDITABLES = ['servicio_id', 'cantidad', 'precio_unitario', 'metodoPago', 'estado'];

const ORDEN_VACIA = {
  servicio_id: '',
  cantidad: 1,
  precio_unitario: '',
  metodoPago: '',
  estado: 'pendiente',
};

// ── Formateadores ────────────────────────────────────────────────────────────
const fmtFecha = (valor) => {
  if (!valor) return '—';
  const s = String(valor);
  // Un `date` de Postgres ("2025-10-15") con `new Date()` se corre un día en
  // Argentina, así que esas fechas se arman a mano.
  const soloFecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.slice(0, 10));
  const base = soloFecha
    ? new Date(Number(soloFecha[1]), Number(soloFecha[2]) - 1, Number(soloFecha[3]))
    : new Date(s);
  if (Number.isNaN(base.getTime())) return '—';
  return base.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtPrecio = (valor) => {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
};

const estadoColor = (estado) => {
  const map = {
    pendiente: '#f59e0b',
    confirmada: '#10b981',
    completada: '#6366f1',
    cancelada: '#ef4444',
    cancelado: '#ef4444',
    en_proceso: '#8b5cf6',
    en_roceso: '#8b5cf6',
    pagado: '#10b981',
    pagada: '#10b981',
  };
  return map[(estado || '').toLowerCase()] || '#94a3b8';
};

// OJO: solo devuelve estilos inline. La clase `pill` va por `className`:
// `styles.pill` es un string, y expandirlo dentro de `style` genera
// propiedades indexadas ({0:'p', 1:'i', ...}) que React no puede aplicar.
const estadoPill = (estado) => ({
  background: `${estadoColor(estado)}1f`,
  color: estadoColor(estado),
});

// La videollamada con Sole se guarda en columnas propias de la reserva
// (`videollamada_*`), aparte de `dia`/`horario`, que son los del evento.
const MODALIDADES_VIDEO = {
  Virtual: 'Videollamada',
  Presencial: 'Presencial',
};

const tieneVideollamada = (r) =>
  Boolean(r?.videollamada_dia || r?.videollamada_horario || r?.videollamada_modalidad);

const videollamadaEstado = (r) => (r?.videollamada_estado || 'pendiente').toLowerCase();

const CAMPOS_CLIENTE_VACIOS = CAMPOS_CLIENTE.reduce((acc, k) => ({ ...acc, [k]: '' }), {});

const borradorDesdeCliente = (cliente) =>
  CAMPOS_CLIENTE.reduce((acc, k) => ({ ...acc, [k]: cliente?.[k] ?? '' }), CAMPOS_CLIENTE_VACIOS);

const borradorDesdeOrden = (orden) => {
  const base = { ...ORDEN_VACIA };
  if (!orden) return base;
  CAMPOS_ORDEN_EDITABLES.forEach((k) => {
    base[k] = orden[k] ?? '';
  });
  return base;
};

// ── Componente ───────────────────────────────────────────────────────────────
export default function FichaCliente({ cliente, onClose, onClienteActualizado }) {
  const [activeTab, setActiveTab] = useState('ficha');
  const [ficha, setFicha] = useState({
    ordenes: [],
    reservas: [],
    sesiones: [],
    inspiraciones: [],
    servicios: [],
  });
  const [fotosPorSesion, setFotosPorSesion] = useState({});
  const [loading, setLoading] = useState(true);
  const [avisos, setAvisos] = useState([]); // warnings no bloqueantes de la carga
  const [guardando, setGuardando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [creandoOrden, setCreandoOrden] = useState(false);
  const [showNuevaOrden, setShowNuevaOrden] = useState(false);
  const [nuevaOrden, setNuevaOrden] = useState(ORDEN_VACIA);

  // Datos del cliente y de la orden, editables en línea. Se siembran con el
  // cliente/orden seleccionado y se mantienen aunque se recargue la lista.
  const [borrador, setBorrador] = useState(() => borradorDesdeCliente(cliente));
  const [borradorOrden, setBorradorOrden] = useState(() => borradorDesdeOrden(null));
  const [ordenContratoId, setOrdenContratoId] = useState(null);
  const [evento, setEvento] = useState({ fecha: '', hora: '', lugar: '' });

  useEffect(() => {
    setBorrador(borradorDesdeCliente(cliente));
  }, [cliente]);

  const avisar = useCallback((tipo, texto) => {
    setAvisos((prev) => [...prev.filter((a) => a.texto !== texto), { tipo, texto }]);
  }, []);

  const descartarAviso = useCallback((texto) => {
    setAvisos((prev) => prev.filter((a) => a.texto !== texto));
  }, []);

  // ── Carga ─────────────────────────────────────────────────────────────────
  const cargarFicha = useCallback(
    async (usuarioId) => {
      setLoading(true);
      setAvisos([]);
      try {
        const [ordenesRes, reservasRes, sesionesRes, favoritosRes, serviciosRes] = await Promise.all([
          supabase
            .from('ordenes')
            .select(SELECT_ORDEN)
            .eq('usuario_id', usuarioId)
            .order('fecha_creacion', { ascending: false }),

          supabase
            .from('reservas')
            .select('*, servicios(id, nombre, lugar)')
            .eq('usuario_id', usuarioId)
            .order('created_at', { ascending: false }),

          // `sesiones.usuario_id` no tiene FK declarada, pero el filtro por
          // columna sí funciona: es el embed `usuarios(...)` lo que no.
          supabase
            .from('sesiones')
            .select(SELECT_SESION)
            .eq('usuario_id', usuarioId)
            .order('fecha', { ascending: false }),

          // `favoritos_usuario.imagen_id` no tiene FK contra
          // `imagenes_inspiracion`, asi que el embed no existe en el schema
          // cache. Los metadatos de la imagen se resuelven mas abajo con una
          // segunda consulta.
          supabase
            .from('favoritos_usuario')
            .select('id, imagen_id, created_at')
            .eq('usuario_id', usuarioId)
            .order('created_at', { ascending: false }),

          supabase
            .from('servicios')
            .select('id, nombre, precio, descripcion, categoria, lugar')
            .order('nombre', { ascending: true }),
        ]);

        // Cada consulta es independiente: si una falla se avisa, pero el resto de
        // la ficha se sigue mostrando en vez de quedar todo en blanco.
        const errores = [
          ['las órdenes', ordenesRes],
          ['las reservas', reservasRes],
          ['las sesiones', sesionesRes],
          ['las inspiraciones', favoritosRes],
          ['el catálogo de servicios', serviciosRes],
        ]
          .filter(([, res]) => res.error)
          .map(([que, res]) => `No se pudieron cargar ${que}: ${res.error.message}`);

        const servicios = serviciosRes.data || [];
        const serviciosMap = Object.fromEntries(servicios.map((s) => [s.id, s]));
        const ordenes = (ordenesRes.data || []).map((o) => ({
          ...o,
          usuarios: null, // el cliente entero viene por props
          servicios: serviciosMap[o.servicio_id] || null,
        }));

        // Inspiraciones: se trae la foto aparte y se re-arma la forma que espera
        // el render (`fav.imagenes_inspiracion`). Sin FK no hay `inner join`
        // disponible, asi que una consulta por ids no mas.
        const favoritos = favoritosRes.data || [];
        let inspiraciones = favoritos;
        if (favoritos.length > 0) {
          const { data: imgsData, error: imgsErr } = await supabase
            .from('imagenes_inspiracion')
            .select('id, titulo, url_publica, ruta_imagen, creditos')
            .in('id', favoritos.map((f) => f.imagen_id));

          if (imgsErr) {
            errores.push(`No se pudieron cargar las fotos de las inspiraciones: ${imgsErr.message}`);
          } else {
            const imgsMap = Object.fromEntries((imgsData || []).map((i) => [i.id, i]));
            inspiraciones = favoritos.map((f) => ({
              ...f,
              imagenes_inspiracion: imgsMap[f.imagen_id] || null,
            }));
          }
        }

        setFicha({
          ordenes,
          reservas: reservasRes.data || [],
          sesiones: sesionesRes.data || [],
          inspiraciones,
          servicios,
        });

        // Fotos de las sesiones, en una sola consulta para no hacer N+1.
        const sesionIds = (sesionesRes.data || []).map((s) => s.id);
        const agrupadas = {};
        if (sesionIds.length > 0) {
          const { data: fotosData, error: fotosErr } = await supabase
            .from('fotos')
            .select(SELECT_FOTOS)
            .in('sesion_id', sesionIds)
            .order('orden', { ascending: true });

          if (fotosErr) {
            errores.push(`No se pudieron cargar las fotos de las sesiones: ${fotosErr.message}`);
          } else {
            (fotosData || []).forEach((f) => {
              if (!agrupadas[f.sesion_id]) agrupadas[f.sesion_id] = [];
              agrupadas[f.sesion_id].push(f);
            });
          }
        }
        setFotosPorSesion(agrupadas);

        if (errores.length > 0) errores.forEach((e) => avisar('warn', e));
      } catch (err) {
        console.error('Error al cargar la ficha del cliente:', err);
        avisar('error', 'No se pudo cargar la ficha: ' + (err?.message || err));
      } finally {
        setLoading(false);
      }
    },
    [avisar]
  );

  useEffect(() => {
    if (cliente?.id) cargarFicha(cliente.id);
  }, [cliente?.id, cargarFicha]);

  // ── Derivados ─────────────────────────────────────────────────────────────
  const { ordenes, reservas, sesiones, inspiraciones, servicios } = ficha;

  // La orden por defecto es la más reciente que no esté cancelada.
  const ordenPorDefecto = useMemo(() => {
    const activas = ordenes.filter((o) => o.estado !== 'cancelada');
    return activas[0] || ordenes[0] || null;
  }, [ordenes]);

  const ordenContrato = useMemo(
    () => ordenes.find((o) => o.id === ordenContratoId) || ordenPorDefecto,
    [ordenes, ordenContratoId, ordenPorDefecto]
  );

  useEffect(() => {
    setBorradorOrden(borradorDesdeOrden(ordenContrato));
  }, [ordenContrato]);

  // Reserva que más probablemente corresponde a la orden elegida: primero se
  // busca una del mismo servicio y, si no hay, la próxima no cancelada.
  const reservaSugerida = useMemo(() => {
    if (reservas.length === 0) return null;
    const activas = reservas.filter((r) => r.estado !== 'cancelada' && r.estado !== 'cancelado');
    const pool = activas.length > 0 ? activas : reservas;
    if (!ordenContrato) return pool[0];
    return pool.find((r) => r.servicio_id === ordenContrato.servicio_id) || pool[0];
  }, [reservas, ordenContrato]);

  // La fecha y el lugar salen de la reserva, así el PDF no arranca con huecos.
  // Solo se recalcula cuando cambia la orden o la reserva sugerida, para no
  // pisar lo que el admin ya escribió a mano.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!reservaSugerida) {
      setEvento({ fecha: '', hora: '', lugar: '' });
      return;
    }
    setEvento({
      fecha: reservaSugerida.dia || '',
      hora: reservaSugerida.horario || '',
      lugar: reservaSugerida.servicios?.lugar || '',
    });
  }, [ordenContrato?.id, reservaSugerida?.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Orden tal como se ve ahora, con los borradores todavía sin guardar. Así la
  // lista de faltantes y el PDF usan lo que el admin acaba de tipear.
  const ordenVista = useMemo(() => {
    if (!ordenContrato) return null;
    return {
      ...ordenContrato,
      ...borradorOrden,
      usuarios: { ...(ordenContrato.usuarios || {}), ...borrador },
      servicios:
        borradorOrden.servicio_id != null && borradorOrden.servicio_id !== ''
          ? servicios.find((s) => s.id === Number(borradorOrden.servicio_id)) || null
          : ordenContrato.servicios,
    };
  }, [ordenContrato, borradorOrden, borrador, servicios]);

  const faltanCliente = useMemo(
    () => CAMPOS_CLIENTE.filter((k) => !(borrador[k] || '').trim()),
    [borrador]
  );

  const faltan = useMemo(
    () => (ordenVista ? datosFaltantes(ordenVista, { exigirEvento: true, evento }) : faltanCliente),
    [ordenVista, evento, faltanCliente]
  );

  const totalGastado = useMemo(
    () => ordenes.reduce((acc, o) => acc + (obtenerMonto(o) || 0), 0),
    [ordenes]
  );

  const serviciosDistintos = useMemo(
    () => Array.from(new Set(ordenes.map((o) => o.servicios?.nombre).filter(Boolean))),
    [ordenes]
  );

  const sesionTotalFotos = useMemo(
    () => sesiones.reduce((acc, s) => acc + (fotosPorSesion[s.id]?.length || 0), 0),
    [sesiones, fotosPorSesion]
  );

  // La reserva futura más cercana. `reservas` viene ordenada por `created_at`
  // descendente, no por fecha, así que hay que ordenarla por `dia`.
  const proximaReserva = useMemo(() => {
    const hoy = new Date().toISOString().slice(0, 10);
    return (
      reservas
        .filter((r) => (r.dia || '') >= hoy && r.estado !== 'cancelada' && r.estado !== 'cancelado')
        .slice()
        .sort((a, b) => String(a.dia).localeCompare(String(b.dia)))[0] || null
    );
  }, [reservas]);

  // Videollamadas pedidas por el cliente desde /contrato, de la más próxima a la
  // más lejana.
  const videollamadas = useMemo(
    () =>
      reservas
        .filter(tieneVideollamada)
        .slice()
        .sort((a, b) =>
          String(a.videollamada_dia || '9999').localeCompare(String(b.videollamada_dia || '9999'))
        ),
    [reservas]
  );

  const videollamadaProxima = useMemo(
    () => videollamadas.find((r) => videollamadaEstado(r) !== 'cancelada') || null,
    [videollamadas]
  );

  const videollamadasPendientes = useMemo(
    () => videollamadas.filter((r) => videollamadaEstado(r) === 'pendiente').length,
    [videollamadas]
  );

  // ── Acciones ──────────────────────────────────────────────────────────────
  const guardarDatosCliente = useCallback(async () => {
    const payload = {};
    CAMPOS_CLIENTE.forEach((campo) => {
      payload[campo] = (borrador[campo] || '').trim() || null;
    });

    const { data, error } = await supabase
      .from('usuarios')
      .update(payload)
      .eq('id', cliente.id)
      .select('id, nombre, email, dni, telefono, direccion')
      .single();

    if (error) throw error;
    return data;
  }, [borrador, cliente?.id]);

  const guardarDatosOrden = useCallback(async () => {
    if (!ordenContrato) return;

    const d = borradorOrden;
    const cantidad = d.cantidad === '' || d.cantidad == null ? null : Number(d.cantidad);
    const unitario = d.precio_unitario === '' || d.precio_unitario == null ? null : Number(d.precio_unitario);

    const payload = {
      servicio_id: d.servicio_id === '' || d.servicio_id == null ? null : Number(d.servicio_id),
      cantidad: Number.isFinite(cantidad) ? cantidad : null,
      precio_unitario: Number.isFinite(unitario) ? unitario : null,
      metodoPago: (d.metodoPago || '').trim() || null,
      estado: d.estado || null,
    };

    // El monto total sale de cantidad × unitario, pero si falta alguno de los
    // dos conservamos el valor anterior en vez de mandarle un 0.
    if (Number.isFinite(cantidad) && Number.isFinite(unitario) && cantidad > 0 && unitario > 0) {
      payload.montoTotal = cantidad * unitario;
    } else if (Number.isFinite(ordenContrato.montoTotal)) {
      payload.montoTotal = ordenContrato.montoTotal;
    }

    const { data, error } = await supabase
      .from('ordenes')
      .update(payload)
      .eq('id', ordenContrato.id)
      .select(SELECT_ORDEN);

    if (error) throw error;
    if (!data || data.length === 0) {
      throw new Error('No se pudo actualizar la orden. Revisá los permisos (RLS).');
    }

    const fila = data[0];
    const servicio = servicios.find((s) => s.id === fila.servicio_id) || null;
    setFicha((prev) => ({
      ...prev,
      ordenes: prev.ordenes.map((o) => (o.id === fila.id ? { ...o, ...fila, servicios: servicio } : o)),
    }));
  }, [borradorOrden, ordenContrato, servicios]);

  // Marcar la videollamada de una reserva como confirmada / cancelada, o
  // devuelve el horario a "pendiente" para que vuelva a avisar.
  const actualizarVideollamada = useCallback(async (reservaId, estado) => {
    const { data, error } = await supabase
      .from('reservas')
      .update({ videollamada_estado: estado })
      .eq('id', reservaId)
      .select('id, videollamada_estado');

    if (error) throw error;
    if (!data || data.length === 0) {
      throw new Error('No se pudo actualizar la videollamada. Revisá los permisos (RLS).');
    }

    const fila = data[0];
    setFicha((prev) => ({
      ...prev,
      reservas: prev.reservas.map((r) =>
        r.id === reservaId ? { ...r, videollamada_estado: fila.videollamada_estado } : r
      ),
    }));
  }, []);

  const guardarTodo = useCallback(async () => {
    setGuardando(true);
    try {
      const clienteGuardado = await guardarDatosCliente();
      await guardarDatosOrden();
      if (clienteGuardado) onClienteActualizado?.(cliente.id, clienteGuardado);
      avisar('ok', 'Datos guardados.');
      return true;
    } catch (err) {
      console.error('Error al guardar la ficha:', err);
      avisar('error', 'No se pudieron guardar los datos: ' + (err?.message || err));
      return false;
    } finally {
      setGuardando(false);
    }
  }, [guardarDatosCliente, guardarDatosOrden, cliente, onClienteActualizado, avisar]);

  const generarContrato = useCallback(async () => {
    if (!ordenVista) return;
    setGenerando(true);
    try {
      // Se guarda antes de generar: si el admin completó un dato para que el
      // PDF salga completo, no queremos que se pierda al cerrar la ficha.
      const clienteGuardado = await guardarDatosCliente();
      await guardarDatosOrden();
      if (clienteGuardado) onClienteActualizado?.(cliente.id, clienteGuardado);

      const { faltan: faltanFinales } = generarContratoPDF(ordenVista, { evento });

      if (faltanFinales.length > 0) {
        avisar(
          'warn',
          `El PDF se descargó con ${faltanFinales.length} dato(s) en blanco: ${faltanFinales.join(' · ')}`
        );
      } else {
        avisar('ok', 'Contrato generado completo.');
      }
    } catch (err) {
      console.error('Error generando el contrato desde la ficha:', err);
      avisar('error', 'No se pudo generar el contrato: ' + (err?.message || err));
    } finally {
      setGenerando(false);
    }
  }, [ordenVista, evento, guardarDatosCliente, guardarDatosOrden, cliente, onClienteActualizado, avisar]);

  const pedirDatos = useCallback(() => {
    const referencia = ordenVista || { id: null, usuarios: borrador, servicios: null };
    const canal = contactarClientePorDatos(referencia, { faltan, evento });

    if (!canal) {
      avisar(
        'error',
        'Este cliente no tiene teléfono ni email cargados. Pedile que te los pase o completalos en el formulario de arriba.'
      );
      return;
    }
    avisar('ok', `Mensaje abierto por ${canal === 'whatsapp' ? 'WhatsApp' : 'email'}.`);
  }, [ordenVista, borrador, faltan, evento, avisar]);

  const contactoRapido = useCallback(
    (tipo) => {
      const tel = (borrador.telefono || '').trim();
      const mail = (borrador.email || '').trim();
      const nombre = (borrador.nombre || '').trim();

      if (tipo === 'llamar') {
        const num = tel.replace(/[^\d+]/g, '');
        if (!num) {
          avisar('warn', 'Cargá el teléfono del cliente para poder llamarlo.');
          return;
        }
        window.open(`tel:${num}`);
        return;
      }

      const mensaje = `¡Hola ${nombre || '!'}! Te escribo de parte de *SC Group*.`;
      const canal = abrirContacto({ telefono: tel, email: mail, asunto: 'SC Group', mensaje });
      if (!canal) {
        avisar('warn', 'Cargá al menos el teléfono o el email del cliente para contactarlo.');
      }
    },
    [borrador, avisar]
  );

  const crearOrden = useCallback(async () => {
    if (!nuevaOrden.servicio_id) {
      avisar('warn', 'Elegí el servicio que contrató el cliente.');
      return;
    }
    setCreandoOrden(true);
    try {
      const servicio = servicios.find((s) => s.id === Number(nuevaOrden.servicio_id));
      const cantidad = Number(nuevaOrden.cantidad) || 1;
      const unitario =
        nuevaOrden.precio_unitario === '' || nuevaOrden.precio_unitario == null
          ? Number(servicio?.precio) || 0
          : Number(nuevaOrden.precio_unitario);

      const payload = {
        usuario_id: cliente.id,
        servicio_id: Number(nuevaOrden.servicio_id),
        cantidad,
        precio_unitario: unitario,
        montoTotal: cantidad * unitario,
        metodoPago: (nuevaOrden.metodoPago || '').trim() || null,
        estado: nuevaOrden.estado || 'pendiente',
      };

      const { data, error } = await supabase.from('ordenes').insert([payload]).select(SELECT_ORDEN);
      if (error) throw error;
      if (!data || data.length === 0) throw new Error('No se pudo crear la orden.');

      const nueva = { ...data[0], usuarios: null, servicios: servicio || null };
      setFicha((prev) => ({ ...prev, ordenes: [nueva, ...prev.ordenes] }));
      setOrdenContratoId(nueva.id);
      setShowNuevaOrden(false);
      setNuevaOrden(ORDEN_VACIA);
      avisar('ok', `Orden #${nueva.id} creada. Ya podés generar el contrato.`);
    } catch (err) {
      console.error('Error creando la orden:', err);
      avisar('error', 'No se pudo crear la orden: ' + (err?.message || err));
    } finally {
      setCreandoOrden(false);
    }
  }, [nuevaOrden, servicios, cliente, avisar]);

  const usarDatosDeReserva = useCallback(() => {
    if (!reservaSugerida) {
      avisar('warn', 'Este cliente no tiene reservas para tomar la fecha y el lugar.');
      return;
    }
    setEvento({
      fecha: reservaSugerida.dia || '',
      hora: reservaSugerida.horario || '',
      lugar: reservaSugerida.servicios?.lugar || '',
    });
  }, [reservaSugerida, avisar]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (!cliente) return null;

  const tabs = [
    { id: 'ficha', label: 'Ficha', Icon: User, alert: faltan.length > 0 ? faltan.length : null },
    { id: 'servicios', label: 'Servicios y órdenes', Icon: Sparkles, count: ordenes.length },
    {
      id: 'reservas',
      label: 'Reservas',
      Icon: Calendar,
      count: reservas.length,
      // Chispa extra cuando hay una videollamada sin confirmar: es lo que hay
      // que responder, y no se ve sin entrar a la pestaña.
      badge: videollamadasPendientes > 0 ? videollamadasPendientes : null,
    },
    { id: 'sesiones', label: 'Sesiones', Icon: Camera, count: sesiones.length },
    { id: 'inspiraciones', label: 'Inspiraciones', Icon: Images, count: inspiraciones.length },
  ];

  return (
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <div className={styles.avatar}>{(cliente.nombre || cliente.email || 'C')[0].toUpperCase()}</div>
            <div className={styles.headerNames}>
              <h2 className={styles.headerName}>{cliente.nombre || 'Cliente sin nombre'}</h2>
              <div className={styles.headerSub}>
                {cliente.email && <span>{cliente.email}</span>}
                {cliente.telefono && <span>{cliente.telefono}</span>}
                {cliente.dni ? (
                  <span>DNI {cliente.dni}</span>
                ) : (
                  <span className={styles.faltaTag}>Falta DNI</span>
                )}
              </div>
            </div>
          </div>

          <div className={styles.headerActions}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnIcon} ${styles.btnWhats}`}
              onClick={() => contactoRapido('whatsapp')}
              title="Abrir WhatsApp"
              aria-label="Abrir WhatsApp"
            >
              <MessageCircle size={16} />
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnIcon} ${styles.btnGhost}`}
              onClick={() => window.open(`mailto:${cliente.email || ''}`)}
              title={cliente.email ? `Escribir a ${cliente.email}` : 'Sin email cargado'}
              aria-label="Enviar email"
            >
              <Mail size={16} />
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnIcon} ${styles.btnGhost}`}
              onClick={() => contactoRapido('llamar')}
              title={cliente.telefono ? `Llamar al ${cliente.telefono}` : 'Sin teléfono cargado'}
              aria-label="Llamar"
            >
              <Phone size={16} />
            </button>
            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className={styles.tabsBar} role="tablist">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activeTab === t.id}
              className={`${styles.tabBtn} ${activeTab === t.id ? styles.tabBtnActive : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              <t.Icon size={16} aria-hidden="true" />
              <span>{t.label}</span>
              {t.alert != null && (
                <span className={`${styles.badge} ${styles.badgeAlert}`} title="Datos faltantes para el contrato">
                  {t.alert}
                </span>
              )}
              {t.count != null && t.alert == null && (
                <span
                  className={styles.badge}
                  style={{
                    background: activeTab === t.id ? '#6366f1' : '#e2e8f0',
                    color: activeTab === t.id ? '#fff' : '#475569',
                  }}
                >
                  {t.count}
                </span>
              )}
              {t.badge != null && (
                <span
                  className={styles.badge}
                  style={{ background: '#CC3399', color: '#fff' }}
                  title="Videollamadas sin confirmar"
                >
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Cuerpo ── */}
        <div className={styles.body}>
          {avisos.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {avisos.map((a) => (
                <div key={a.texto} className={`${styles.alert} ${styles[`alert${a.tipo === 'ok' ? 'Ok' : a.tipo === 'warn' ? 'Warn' : 'Error'}`]}`}>
                  {a.tipo === 'ok' ? <Check size={16} /> : <AlertTriangle size={16} />}
                  <div className={styles.alertBody}>{a.texto}</div>
                  <button
                    type="button"
                    className={styles.closeBtn}
                    style={{ width: 24, height: 24 }}
                    onClick={() => descartarAviso(a.texto)}
                    aria-label="Cerrar aviso"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <div className={styles.loading}>Cargando la ficha del cliente…</div>
          ) : (
            <>
              {/* ═══ FICHA ═══ */}
              {activeTab === 'ficha' && (
                <div className={styles.gridTwo}>
                  {/* ── Columna izquierda: datos + historial ── */}
                  <div className={styles.column}>
                    <section className={styles.section}>
                      <div className={styles.sectionHead}>
                        <h3 className={styles.sectionTitle}>
                          <User size={14} /> Datos del cliente
                        </h3>
                        {faltanCliente.length === 0 ? (
                          <span className={`${styles.pill} ${styles.pillNeutral}`}>Completos</span>
                        ) : (
                          <span className={styles.faltaTag}>
                            {faltanCliente.length} sin cargar
                          </span>
                        )}
                      </div>
                      <div className={styles.sectionBody}>
                        <div className={styles.fieldRow}>
                          {CAMPOS_CLIENTE.map((campo) => {
                            const falta = !(borrador[campo] || '').trim();
                            return (
                              <div className={styles.field} key={campo}>
                                <label className={styles.label} htmlFor={`ficha-${campo}`}>
                                  {ETIQUETAS_CLIENTE[campo]}
                                  {falta && <span className={styles.faltaTag}>Falta</span>}
                                </label>
                                <input
                                  id={`ficha-${campo}`}
                                  className={`${styles.input} ${falta ? styles.inputMissing : ''}`}
                                  type={campo === 'email' ? 'email' : 'text'}
                                  placeholder={falta ? 'Pendiente de confirmar' : ''}
                                  value={borrador[campo] || ''}
                                  onChange={(e) => setBorrador((prev) => ({ ...prev, [campo]: e.target.value }))}
                                />
                              </div>
                            );
                          })}
                        </div>
                        <div className={styles.btnRow} style={{ marginTop: 12 }}>
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnGhost}`}
                            onClick={guardarTodo}
                            disabled={guardando}
                          >
                            <Save size={15} /> {guardando ? 'Guardando…' : 'Guardar cambios'}
                          </button>
                        </div>
                        <p className={styles.hint} style={{ marginTop: 8 }}>
                          El DNI y el domicilio son los que más se piden: van al contrato y hay que
                          tenerlos a mano antes de que el cliente venga.
                        </p>
                      </div>
                    </section>

                    <section className={styles.section}>
                      <div className={styles.sectionHead}>
                        <h3 className={styles.sectionTitle}>
                          <Sparkles size={14} /> Resumen
                        </h3>
                      </div>
                      <div className={styles.sectionBody}>
                        <div className={styles.grid}>
                          <div className={styles.metric}>
                            <span className={styles.metricLabel}>Servicio elegido</span>
                            <span className={styles.metricValue}>
                              {obtenerServicio(ordenVista)?.nombre ||
                                (ordenes.length === 0 ? 'Sin orden' : 'Sin servicio asignado')}
                            </span>
                            {serviciosDistintos.length > 1 && (
                              <span className={styles.hint}>{serviciosDistintos.join(' · ')}</span>
                            )}
                          </div>
                          <div className={styles.metric}>
                            <span className={styles.metricLabel}>Total contratado</span>
                            <span className={`${styles.metricValue} ${styles.metricAccent}`}>
                              {fmtPrecio(totalGastado)}
                            </span>
                            <span className={styles.hint}>
                              {ordenes.length} {ordenes.length === 1 ? 'orden' : 'órdenes'}
                            </span>
                          </div>
                          <div className={styles.metric}>
                            <span className={styles.metricLabel}>Próxima reserva</span>
                            <span className={styles.metricValueSm}>
                              {proximaReserva
                                ? `${fmtFecha(proximaReserva.dia)}${proximaReserva.horario ? ` · ${proximaReserva.horario}` : ''}`
                                : 'Sin reservas futuras'}
                            </span>
                            {proximaReserva?.servicios?.nombre && (
                              <span className={styles.hint}>{proximaReserva.servicios.nombre}</span>
                            )}
                          </div>
                          <div className={styles.metric}>
                            <span className={styles.metricLabel}>Sesiones</span>
                            <span className={styles.metricValue}>
                              {sesiones.length}{' '}
                              <span className={styles.hint}>
                                ({sesionTotalFotos} {sesionTotalFotos === 1 ? 'foto' : 'fotos'})
                              </span>
                            </span>
                          </div>
                          <div className={styles.metric}>
                            <span className={styles.metricLabel}>Videollamada con Sole</span>
                            <span
                              className={`${styles.metricValueSm} ${
                                videollamadasPendientes > 0 ? styles.metricAccent : ''
                              }`}
                            >
                              {videollamadaProxima
                                ? `${fmtFecha(videollamadaProxima.videollamada_dia)}${
                                    videollamadaProxima.videollamada_horario
                                      ? ` · ${videollamadaProxima.videollamada_horario}`
                                      : ''
                                  }`
                                : 'Sin videollamada pedida'}
                            </span>
                            <span className={styles.hint}>
                              {videollamadaProxima
                                ? `${
                                    MODALIDADES_VIDEO[videollamadaProxima.videollamada_modalidad] ||
                                    videollamadaProxima.videollamada_modalidad ||
                                    'Virtual'
                                  } · ${videollamadaEstado(videollamadaProxima)}`
                                : 'Se pide al completar el contrato'}
                            </span>
                          </div>
                          <div className={styles.metric}>
                            <span className={styles.metricLabel}>Inspiraciones</span>
                            <span className={styles.metricValue}>{inspiraciones.length}</span>
                          </div>
                          <div className={styles.metric}>
                            <span className={styles.metricLabel}>Cliente desde</span>
                            <span className={styles.metricValueSm}>
                              {ordenes[ordenes.length - 1]?.fecha_creacion
                                ? fmtFecha(ordenes[ordenes.length - 1].fecha_creacion)
                                : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>

                  {/* ── Columna derecha: contrato ── */}
                  <div className={styles.column}>
                    <section className={styles.section}>
                      <div className={styles.sectionHead}>
                        <h3 className={styles.sectionTitle}>
                          <FileText size={14} /> Contrato
                        </h3>
                        {faltan.length === 0 ? (
                          <span className={`${styles.pill} ${styles.pillNeutral}`}>Listo para firmar</span>
                        ) : (
                          <span className={styles.faltaTag}>Faltan {faltan.length}</span>
                        )}
                      </div>
                      <div className={styles.sectionBodyTight}>
                        {/* Elegir para qué orden es el contrato */}
                        {ordenes.length === 0 ? (
                          <div className={`${styles.alert} ${styles.alertWarn}`}>
                            <AlertTriangle size={16} />
                            <div className={styles.alertBody}>
                              <span className={styles.alertTitle}>Este cliente no tiene ninguna orden</span>
                              El contrato sale de una orden: es lo que define el servicio, el precio
                              y el método de pago. Creala abajo y seguí.
                            </div>
                          </div>
                        ) : (
                          <div className={styles.field}>
                            <span className={styles.label}>Orden del contrato</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {ordenes.map((o) => {
                                const activa = ordenVista?.id === o.id;
                                return (
                                  <button
                                    key={o.id}
                                    type="button"
                                    className={`${styles.ordenOption} ${activa ? styles.ordenOptionActive : ''}`}
                                    onClick={() => setOrdenContratoId(o.id)}
                                  >
                                    <span
                                      className={`${styles.radio} ${activa ? styles.radioOn : ''}`}
                                      aria-hidden="true"
                                    >
                                      {activa && <span className={styles.radioDot} />}
                                    </span>
                                    <span style={{ flex: 1, minWidth: 0 }}>
                                      <span className={styles.rowTitle}>
                                        {obtenerServicio(o)?.nombre || `Orden #${o.id}`}
                                      </span>
                                      <span className={styles.metaRow}>
                                        <span className={styles.metaItem}>
                                          #{o.id} · {fmtFecha(o.fecha_creacion)}
                                        </span>
                                        <span className={styles.metaItem}>{fmtPrecio(obtenerMonto(o))}</span>
                                      </span>
                                    </span>
                                    <span className={styles.pill} style={estadoPill(o.estado)}>{ESTADO_LABELS[o.estado] || o.estado || '—'}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Datos de la orden que van al contrato */}
                        {ordenVista && (
                          <>
                            <div className={styles.divider} />
                            <div className={styles.fieldRow}>
                              <div className={styles.field}>
                                <label className={styles.label} htmlFor="ficha-servicio">
                                  Servicio
                                  {!ordenVista.servicios?.nombre && <span className={styles.faltaTag}>Falta</span>}
                                </label>
                                <select
                                  id="ficha-servicio"
                                  className={styles.select}
                                  value={borradorOrden.servicio_id ?? ''}
                                  onChange={(e) =>
                                    setBorradorOrden((prev) => ({ ...prev, servicio_id: e.target.value }))
                                  }
                                >
                                  <option value="">— Sin servicio —</option>
                                  {servicios.map((s) => (
                                    <option key={s.id} value={s.id}>
                                      {s.nombre}
                                      {s.precio != null ? ` — ${fmtPrecio(s.precio)}` : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className={styles.field}>
                                <label className={styles.label} htmlFor="ficha-estado">
                                  Estado de la orden
                                </label>
                                <select
                                  id="ficha-estado"
                                  className={styles.select}
                                  value={borradorOrden.estado ?? ''}
                                  onChange={(e) =>
                                    setBorradorOrden((prev) => ({ ...prev, estado: e.target.value }))
                                  }
                                >
                                  <option value="">Sin estado</option>
                                  {ESTADOS.map((e) => (
                                    <option key={e} value={e}>
                                      {ESTADO_LABELS[e]}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className={styles.field}>
                                <label className={styles.label} htmlFor="ficha-cantidad">
                                  Cantidad
                                  {!ordenVista.cantidad && <span className={styles.faltaTag}>Falta</span>}
                                </label>
                                <input
                                  id="ficha-cantidad"
                                  className={`${styles.input} ${!ordenVista.cantidad ? styles.inputMissing : ''}`}
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={borradorOrden.cantidad ?? ''}
                                  onChange={(e) =>
                                    setBorradorOrden((prev) => ({ ...prev, cantidad: e.target.value }))
                                  }
                                />
                              </div>
                              <div className={styles.field}>
                                <label className={styles.label} htmlFor="ficha-unitario">
                                  Precio unitario (USD)
                                </label>
                                <input
                                  id="ficha-unitario"
                                  className={styles.input}
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={borradorOrden.precio_unitario ?? ''}
                                  onChange={(e) =>
                                    setBorradorOrden((prev) => ({ ...prev, precio_unitario: e.target.value }))
                                  }
                                />
                              </div>
                              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                                <label className={styles.label} htmlFor="ficha-metodo">
                                  Método de pago
                                  {!ordenVista.metodoPago && <span className={styles.faltaTag}>Falta</span>}
                                </label>
                                <input
                                  id="ficha-metodo"
                                  className={`${styles.input} ${!ordenVista.metodoPago ? styles.inputMissing : ''}`}
                                  placeholder="Transferencia, efectivo, alias…"
                                  value={borradorOrden.metodoPago ?? ''}
                                  onChange={(e) =>
                                    setBorradorOrden((prev) => ({ ...prev, metodoPago: e.target.value }))
                                  }
                                />
                                <span className={styles.hint}>
                                  Total del contrato:{' '}
                                  <strong>{fmtPrecio(obtenerMonto(ordenVista))}</strong>
                                </span>
                              </div>
                            </div>
                          </>
                        )}

                        {/* Datos del evento para la cláusula PRIMERA */}
                        {ordenVista && (
                          <>
                            <div className={styles.divider} />
                            <div className={styles.fieldRow}>
                              <div className={styles.field}>
                                <label className={styles.label} htmlFor="ficha-evento-fecha">
                                  <Calendar size={12} /> Fecha del evento
                                  {!evento.fecha && <span className={styles.faltaTag}>Falta</span>}
                                </label>
                                <input
                                  id="ficha-evento-fecha"
                                  className={`${styles.input} ${!evento.fecha ? styles.inputMissing : ''}`}
                                  type="date"
                                  value={evento.fecha || ''}
                                  onChange={(e) => setEvento((prev) => ({ ...prev, fecha: e.target.value }))}
                                />
                              </div>
                              <div className={styles.field}>
                                <label className={styles.label} htmlFor="ficha-evento-hora">
                                  <Clock size={12} /> Horario
                                </label>
                                <input
                                  id="ficha-evento-hora"
                                  className={styles.input}
                                  type="time"
                                  value={evento.hora || ''}
                                  onChange={(e) => setEvento((prev) => ({ ...prev, hora: e.target.value }))}
                                />
                              </div>
                              <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                                <label className={styles.label} htmlFor="ficha-evento-lugar">
                                  <MapPin size={12} /> Lugar del evento
                                  {!evento.lugar.trim() && <span className={styles.faltaTag}>Falta</span>}
                                </label>
                                <input
                                  id="ficha-evento-lugar"
                                  className={`${styles.input} ${!evento.lugar.trim() ? styles.inputMissing : ''}`}
                                  placeholder={HUECO}
                                  value={evento.lugar || ''}
                                  onChange={(e) => setEvento((prev) => ({ ...prev, lugar: e.target.value }))}
                                />
                                {reservaSugerida && (
                                  <button
                                    type="button"
                                    className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`}
                                    style={{ alignSelf: 'flex-start' }}
                                    onClick={usarDatosDeReserva}
                                  >
                                    <Calendar size={13} /> Tomar de la reserva
                                    {reservaSugerida.dia ? ` (${fmtFecha(reservaSugerida.dia)})` : ''}
                                  </button>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                        <div className={styles.divider} />

                        {/* Qué falta y cómo resolverlo */}
                        <div className={`${styles.alert} ${faltan.length === 0 ? styles.alertOk : styles.alertWarn}`}>
                          {faltan.length === 0 ? <Check size={16} /> : <AlertTriangle size={16} />}
                          <div className={styles.alertBody}>
                            {faltan.length === 0 ? (
                              <span className={styles.alertTitle}>No falta nada</span>
                            ) : (
                              <>
                                <span className={styles.alertTitle}>
                                  Faltan {faltan.length}{' '}
                                  {faltan.length === 1 ? 'dato' : 'datos'} para el contrato
                                </span>
                                <ul className={styles.faltanList}>
                                  {faltan.map((f) => (
                                    <li key={f} className={styles.faltaChip}>
                                      {f}
                                    </li>
                                  ))}
                                </ul>
                              </>
                            )}
                          </div>
                        </div>

                        <div className={styles.btnRow}>
                          {ordenVista && (
                            <button
                              type="button"
                              className={`${styles.btn} ${styles.btnSuccess}`}
                              onClick={generarContrato}
                              disabled={generando || guardando}
                            >
                              <FileText size={15} /> {generando ? 'Generando…' : 'Generar contrato'}
                            </button>
                          )}
                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnWhats}`}
                            onClick={pedirDatos}
                          >
                            <MessageCircle size={15} />
                            {faltan.length === 0 ? 'Contactar al cliente' : 'Pedirle los datos'}
                          </button>
                          {ordenVista && (
                            <button
                              type="button"
                              className={`${styles.btn} ${styles.btnGhost}`}
                              onClick={guardarTodo}
                              disabled={guardando}
                            >
                              <Save size={15} /> {guardando ? 'Guardando…' : 'Guardar'}
                            </button>
                          )}
                        </div>
                        {ordenVista && (
                          <p className={styles.hint}>
                            Al generar el contrato se guardan los datos que completaste, así no se
                            pierde nada.
                          </p>
                        )}

                        {/* Alta de orden para clientes que no tienen ninguna */}
                        {ordenes.length === 0 && (
                          <>
                            <div className={styles.divider} />
                            {showNuevaOrden ? (
                              <>
                                <div className={styles.fieldRow}>
                                  <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
                                    <label className={styles.label} htmlFor="nueva-servicio">
                                      Servicio contratado
                                    </label>
                                    <select
                                      id="nueva-servicio"
                                      className={styles.select}
                                      value={nuevaOrden.servicio_id}
                                      onChange={(e) =>
                                        setNuevaOrden((prev) => ({
                                          ...prev,
                                          servicio_id: e.target.value,
                                          precio_unitario:
                                            prev.precio_unitario === '' &&
                                            e.target.selectedOptions[0]?.dataset.precio
                                              ? e.target.selectedOptions[0].dataset.precio
                                              : prev.precio_unitario,
                                        }))
                                      }
                                    >
                                      <option value="">— Elegí un servicio —</option>
                                      {servicios.map((s) => (
                                        <option key={s.id} value={s.id} data-precio={s.precio ?? ''}>
                                          {s.nombre}
                                          {s.precio != null ? ` — ${fmtPrecio(s.precio)}` : ''}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className={styles.field}>
                                    <label className={styles.label} htmlFor="nueva-cantidad">
                                      Cantidad
                                    </label>
                                    <input
                                      id="nueva-cantidad"
                                      className={styles.input}
                                      type="number"
                                      min="0"
                                      step="1"
                                      value={nuevaOrden.cantidad}
                                      onChange={(e) =>
                                        setNuevaOrden((prev) => ({ ...prev, cantidad: e.target.value }))
                                      }
                                    />
                                  </div>
                                  <div className={styles.field}>
                                    <label className={styles.label} htmlFor="nueva-unitario">
                                      Precio unitario
                                    </label>
                                    <input
                                      id="nueva-unitario"
                                      className={styles.input}
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={nuevaOrden.precio_unitario}
                                      onChange={(e) =>
                                        setNuevaOrden((prev) => ({ ...prev, precio_unitario: e.target.value }))
                                      }
                                    />
                                  </div>
                                  <div className={styles.field}>
                                    <label className={styles.label} htmlFor="nueva-metodo">
                                      Método de pago
                                    </label>
                                    <input
                                      id="nueva-metodo"
                                      className={styles.input}
                                      placeholder="Transferencia, efectivo…"
                                      value={nuevaOrden.metodoPago}
                                      onChange={(e) =>
                                        setNuevaOrden((prev) => ({ ...prev, metodoPago: e.target.value }))
                                      }
                                    />
                                  </div>
                                  <div className={styles.field}>
                                    <label className={styles.label} htmlFor="nueva-estado">
                                      Estado
                                    </label>
                                    <select
                                      id="nueva-estado"
                                      className={styles.select}
                                      value={nuevaOrden.estado}
                                      onChange={(e) =>
                                        setNuevaOrden((prev) => ({ ...prev, estado: e.target.value }))
                                      }
                                    >
                                      {ESTADOS.map((e) => (
                                        <option key={e} value={e}>
                                          {ESTADO_LABELS[e]}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                                <div className={styles.btnRow}>
                                  <button
                                    type="button"
                                    className={`${styles.btn} ${styles.btnPrimary}`}
                                    onClick={crearOrden}
                                    disabled={creandoOrden}
                                  >
                                    <Plus size={15} /> {creandoOrden ? 'Creando…' : 'Crear orden'}
                                  </button>
                                  <button
                                    type="button"
                                    className={`${styles.btn} ${styles.btnGhost}`}
                                    onClick={() => setShowNuevaOrden(false)}
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </>
                            ) : (
                              <button
                                type="button"
                                className={`${styles.btn} ${styles.btnPrimary} ${styles.btnBlock}`}
                                onClick={() => setShowNuevaOrden(true)}
                              >
                                <Plus size={15} /> Crear la primera orden
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </section>
                  </div>
                </div>
              )}

              {/* ═══ SERVICIOS Y ÓRDENES ═══ */}
              {activeTab === 'servicios' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {ordenes.length === 0 ? (
                    <Empty texto="Este cliente todavía no contrató ningún servicio." />
                  ) : (
                    ordenes.map((o) => {
                      const servicio = o.servicios;
                      return (
                        <div className={styles.row} key={o.id}>
                          <div className={styles.rowMain}>
                            <div className={styles.rowTitle}>
                              {servicio?.nombre || <span className={styles.faltaTag}>Sin servicio asignado</span>}
                            </div>
                            <div className={styles.metaRow}>
                              <span className={styles.metaItem}>
                                <Sparkles size={13} /> #{o.id} · {fmtFecha(o.fecha_creacion)}
                              </span>
                              <span className={styles.metaItem}>
                                <Wallet size={13} /> {fmtPrecio(obtenerMonto(o))}
                              </span>
                              <span className={styles.metaItem}>Cant: {o.cantidad ?? '—'}</span>
                              {o.precio_unitario != null && (
                                <span className={styles.metaItem}>
                                  Unitario: {fmtPrecio(o.precio_unitario)}
                                </span>
                              )}
                              {o.metodoPago && (
                                <span className={styles.metaItem}>
                                  <CreditCard size={13} /> {o.metodoPago}
                                </span>
                              )}
                            </div>
                            {servicio?.descripcion && (
                              <div className={styles.rowText}>{servicio.descripcion}</div>
                            )}
                            {servicio?.categoria && (
                              <div className={styles.metaRow}>
                                <span className={styles.pill} style={estadoPill('confirmada')}>{servicio.categoria}</span>
                              </div>
                            )}
                          </div>
                          <div className={styles.rowSide}>
                            <span className={styles.pill} style={estadoPill(o.estado)}>
                              {ESTADO_LABELS[o.estado] || o.estado || 'sin estado'}
                            </span>
                            <button
                              type="button"
                              className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`}
                              onClick={() => {
                                setOrdenContratoId(o.id);
                                setActiveTab('ficha');
                              }}
                            >
                              <FileText size={13} /> Contrato
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* ═══ RESERVAS ═══ */}
              {activeTab === 'reservas' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {reservas.length === 0 ? (
                    <Empty texto="Este cliente no tiene reservas registradas." />
                  ) : (
                    reservas.map((r) => (
                      <div className={styles.row} key={r.id}>
                        <div className={styles.rowMain}>
                          <div className={styles.rowTitle}>
                            {r.servicios?.nombre || r.tipo_sesion || `Reserva #${r.id}`}
                            {r.id === reservaSugerida?.id && (
                              <span className={`${styles.pill} ${styles.badgeAlert}`}>
                                sugerida para el contrato
                              </span>
                            )}
                          </div>
                          <div className={styles.metaRow}>
                            <span className={styles.metaItem}>
                              <Calendar size={13} /> {fmtFecha(r.dia)}
                            </span>
                            {r.horario && (
                              <span className={styles.metaItem}>
                                <Clock size={13} /> {r.horario}
                              </span>
                            )}
                            {r.servicios?.lugar && (
                              <span className={styles.metaItem}>
                                <MapPin size={13} /> {r.servicios.lugar}
                              </span>
                            )}
                            {r.presupuesto && (
                              <span className={styles.metaItem}>
                                <Wallet size={13} /> {r.presupuesto}
                              </span>
                            )}
                            {r.metodo_pago && (
                              <span className={styles.metaItem}>
                                <CreditCard size={13} /> {r.metodo_pago}
                              </span>
                            )}
                          </div>
                          {r.direccion && <div className={styles.rowText}>Dirección: {r.direccion}</div>}
                          {r.preferencias && <div className={styles.rowText}>Preferencias: {r.preferencias}</div>}
                          {r.experiencia_anterior && (
                            <div className={styles.rowText}>Experiencia previa: {r.experiencia_anterior}</div>
                          )}
                          {r.observaciones && <div className={styles.rowText}>Observaciones: {r.observaciones}</div>}
                          {r.mensaje && <div className={styles.rowText}>Mensaje: {r.mensaje}</div>}

                          {tieneVideollamada(r) && (
                            <div className={styles.rowText}>
                              <span className={`${styles.pill} ${styles.badgeAlert}`}>
                                <Video size={12} /> Videollamada pedida
                              </span>{' '}
                              {MODALIDADES_VIDEO[r.videollamada_modalidad] ||
                                r.videollamada_modalidad ||
                                'Virtual'}{' '}
                              · {fmtFecha(r.videollamada_dia)}
                              {r.videollamada_horario ? ` · ${r.videollamada_horario}` : ''}
                            </div>
                          )}
                        </div>
                        <div className={styles.rowSide}>
                          <span className={styles.pill} style={estadoPill(r.estado)}>{r.estado || 'sin estado'}</span>

                          {tieneVideollamada(r) && (
                            <span className={styles.pill} style={estadoPill(videollamadaEstado(r))}>
                              <Video size={12} /> {videollamadaEstado(r)}
                            </span>
                          )}

                          <span className={styles.hint}>Pedida {fmtFecha(r.created_at)}</span>

                          {tieneVideollamada(r) &&
                            (videollamadaEstado(r) === 'confirmada' ? (
                              <button
                                type="button"
                                className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`}
                                onClick={() =>
                                  actualizarVideollamada(r.id, 'cancelada').catch((e) =>
                                    avisar('error', e.message)
                                  )
                                }
                              >
                                <X size={13} /> Cancelar
                              </button>
                            ) : (
                              <button
                                type="button"
                                className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`}
                                onClick={() =>
                                  actualizarVideollamada(r.id, 'confirmada').catch((e) =>
                                    avisar('error', e.message)
                                  )
                                }
                              >
                                <Check size={13} /> Confirmar
                              </button>
                            ))}

                          <button
                            type="button"
                            className={`${styles.btn} ${styles.btnGhost} ${styles.btnSm}`}
                            onClick={() => {
                              setEvento({
                                fecha: r.dia || '',
                                hora: r.horario || '',
                                lugar: r.servicios?.lugar || '',
                              });
                              setActiveTab('ficha');
                              avisar('ok', 'Datos del evento copiados de la reserva.');
                            }}
                          >
                            <FileText size={13} /> Al contrato
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* ═══ SESIONES ═══ */}
              {activeTab === 'sesiones' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {sesiones.length === 0 ? (
                    <Empty texto="Este cliente todavía no tiene sesiones cargadas." />
                  ) : (
                    sesiones.map((s) => {
                      const fotos = fotosPorSesion[s.id] || [];
                      const portada = fotos[0];
                      return (
                        <div className={styles.row} key={s.id}>
                          {portada ? (
                            <div className={styles.imgWrap} style={{ width: 92, flexShrink: 0 }}>
                              <img
                                src={portada.url_foto}
                                alt={s.nombre || 'Foto de la sesión'}
                                className={styles.imgFrame}
                                style={{ height: 92, borderRadius: 8 }}
                              />
                              {fotos.length > 1 && <span className={styles.imgCount}>{fotos.length}</span>}
                            </div>
                          ) : (
                            <div
                              className={styles.imgPlaceholder}
                              style={{ width: 92, height: 92, borderRadius: 8, flexShrink: 0 }}
                            >
                              <ImageOff size={20} />
                            </div>
                          )}

                          <div className={styles.rowMain}>
                            <div className={styles.rowTitle}>{s.nombre || 'Sesión sin nombre'}</div>
                            <div className={styles.metaRow}>
                              <span className={styles.metaItem}>
                                <Calendar size={13} /> {fmtFecha(s.fecha)}
                              </span>
                              <span className={styles.metaItem}>
                                <Camera size={13} /> {fotos.length}{' '}
                                {fotos.length === 1 ? 'foto' : 'fotos'}
                              </span>
                              {s.subido_google_drive && (
                                <span className={styles.metaItem}>
                                  <Check size={13} /> Drive
                                </span>
                              )}
                            </div>
                            {s.descripcion && <div className={styles.rowText}>{s.descripcion}</div>}
                            {fotos.length === 0 && (
                              <div className={styles.hint}>
                                Todavía no hay fotos cargadas en esta sesión.
                              </div>
                            )}
                          </div>

                          <div className={styles.rowSide}>
                            <span className={styles.pill} style={estadoPill(s.estado)}>{s.estado || 'sin estado'}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* ═══ INSPIRACIONES ═══ */}
              {activeTab === 'inspiraciones' && (
                <div className={styles.imgGrid}>
                  {inspiraciones.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Empty texto="Este cliente no guardó ninguna inspiración." />
                    </div>
                  ) : (
                    inspiraciones.map((fav) => {
                      const img = fav.imagenes_inspiracion;
                      const src = img?.url_publica || img?.ruta_imagen;
                      return (
                        <div className={styles.imgCard} key={fav.id}>
                          {src ? (
                            <img
                              src={src}
                              alt={img?.titulo || 'Inspiración guardada'}
                              className={styles.imgFrame}
                              loading="lazy"
                            />
                          ) : (
                            <div className={styles.imgPlaceholder}>
                              <ImageOff size={24} />
                            </div>
                          )}
                          <div className={styles.imgCaption}>
                            {img?.titulo && <span className={styles.imgCaptionStrong}>{img.titulo}</span>}
                            {img?.creditos && <span>{img.creditos}</span>}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className={styles.footer}>
          <span className={styles.footerText}>
            {faltan.length === 0
              ? 'La ficha está completa: se puede generar el contrato.'
              : `${faltan.length} ${faltan.length === 1 ? 'dato' : 'datos'} pendientes para el contrato.`}
          </span>
          <div className={styles.btnRow}>
            <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={guardarTodo} disabled={guardando}>
              <Save size={15} /> {guardando ? 'Guardando…' : 'Guardar ficha'}
            </button>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Empty({ texto }) {
  return (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>
        <Inbox size={34} />
      </div>
      <p style={{ margin: 0 }}>{texto}</p>
    </div>
  );
}
